import qs from "qs"
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"

import { useLocation } from "@reach/router"
import { useStaticQuery } from "gatsby"
import {
  Box,
  Button,
  Card,
  Container,
  Filter,
  FilterItem,
  Flex,
  GridContainer,
  Heading,
  Input,
  Meta,
  Pagination,
  SectionHeading,
} from "@singita/components"
import algoliasearch from "algoliasearch/lite"
import dayjs from "dayjs"
import { graphql, Link } from "gatsby"
import ResponsiveImage from "./ResponsiveImage"
import { getFluidGatsbyImage } from "gatsby-singita-theme/src/common/images"
import { Media } from "gatsby-singita-theme/src/Media"
import orderBy from "lodash/orderBy"
import {
  Configure,
  connectHierarchicalMenu,
  connectHits,
  connectMenu,
  connectPagination,
  connectSearchBox,
  InstantSearch,
} from "react-instantsearch-dom"
import { ARCHIVE_TYPES } from "../common/constants"
import {
  createSlug,
  generateBlogSlug,
  mapperWithFunction,
} from "../common/utils"
import GridItem from "../components/GridItem"
import LinkWrapper from "../components/Link"
import useAllSitePages_PageExists from "../hooks/useAllSitePagesQuery"

const createURL = state => {
  const query = qs.stringify(state)
  return query ? `?${query}` : ""
}

const searchStateToUrl = searchState =>
  searchState ? createURL(searchState) : ""

const urlToSearchState = location => {
  const searchState = { ...qs.parse(location.search.slice(1)) }

  if (location?.pathname) {
    const pathMatch = location.pathname.match(/\/page\/(\d+)\/?$/)
    if (pathMatch) {
      const pathPage = parseInt(pathMatch[1], 10)
      if (!Number.isNaN(pathPage) && pathPage > 1) {
        searchState.page = pathPage
      }
    }
  }

  if (searchState.page) {
    const pageNumber = Number(searchState.page)
    if (Number.isNaN(pageNumber) || pageNumber <= 1) {
      delete searchState.page
    } else {
      searchState.page = pageNumber
    }
  }

  return searchState
}

const DEBOUNCE_TIME = 400

const ARTICLE_TYPES = {
  BLOG: "Blog Post",
  PRESS: "Press Release",
  WILDLIFE: "Wildlife Report",
  PRESS_FEATURES: "Press Feature",
  BUSH_STORY: "Bush Stories",  // Note: Algolia uses plural
  DIGITAL_MAGAZINE: "Digital Magazine",
}

const ARCHIVE_INDEX_MAP = {
  [ARCHIVE_TYPES.BLOG]: "articles_date_desc",
  [ARCHIVE_TYPES.PRESS_FEATURES]: "articles_date_desc",
  [ARCHIVE_TYPES.PRESS]: "articles_date_desc",
  [ARCHIVE_TYPES.WILDLIFE]: "articles_date_desc",
  [ARCHIVE_TYPES.AUTHOR]: "articles_date_desc",
  [ARCHIVE_TYPES.AWARDS]: "awards",
  [ARCHIVE_TYPES.CAREER]: "career",
  [ARCHIVE_TYPES.DIGITAL_MAGAZINE]: "articles_date_desc",
}

// Deprecate when everything has moved to the new indices
const OLD_ARCHIVE_POST_TYPE_MAP = {
  [ARCHIVE_TYPES.WILDLIFE]: "Wildlife Report",
  [ARCHIVE_TYPES.PRESS]: "Press Release",
  [ARCHIVE_TYPES.PRESS_FEATURES]: "Press Feature",
}

const NEW_ARCHIVE_POST_TYPE_MAP = {
  [ARCHIVE_TYPES.BLOG]: ARTICLE_TYPES.BLOG,
  [ARCHIVE_TYPES.WILDLIFE]: ARTICLE_TYPES.WILDLIFE,
  [ARCHIVE_TYPES.PRESS_FEATURES]: ARTICLE_TYPES.PRESS_FEATURES,
  [ARCHIVE_TYPES.DIGITAL_MAGAZINE]: ARTICLE_TYPES.DIGITAL_MAGAZINE,
}

const ARCHIVE_FILTER_ATTRIBUTE_MAP = {
  [ARCHIVE_TYPES.BLOG]: ["articleType", "category"],
  [ARCHIVE_TYPES.PRESS]: ["year"],
  [ARCHIVE_TYPES.PRESS_FEATURES]: ["year"],
  [ARCHIVE_TYPES.WILDLIFE]: ["articleType", "region", "year_hier.lvl0"],
  [ARCHIVE_TYPES.AWARDS]: ["regions", "year"],
  [ARCHIVE_TYPES.CAREER]: ["regions"],
  [ARCHIVE_TYPES.AUTHOR]: ["author"],
  [ARCHIVE_TYPES.DIGITAL_MAGAZINE]: ["year"],
}

const REFINEMENT_NAME_MAP = {}

const ATTRIBUTE_NAMES = {
  regions: "region",
  region: "region",
  ["year_hier.lvl0"]: "month",
  year: "date",
  articleType: "type",
  category: "category",
}

const getArchiveImage = (image, headline, description) => {
  if (image.includes("https://singita-md.s3.amazonaws.com/")) {
    const key = {
      bucket: "singita-md",
      key: image.replace("https://singita-md.s3.amazonaws.com/", ""),
      edits: {
        resize: {
          width: 735,
          height: 365,
        },
      },
    }
    const src = `https://d2o51t207iusce.cloudfront.net/${btoa(
      JSON.stringify(key)
    )}`

    return (
      <img
        alt={description || headline || null}
        src={src}
        style={{ height: "100%", width: "100%", objectFit: "cover" }}
      />
    )
  }
  const fluid = getFluidGatsbyImage(
    { source: image, width: 735, height: 365 },
    { maxWidth: 735, maxHeight: 365, quality: 90, isContentful: true }
  )
  return (
    <ResponsiveImage
      alt={description || headline || null}
      fluid={fluid}
      objectFit="cover"
      style={{ height: "100%", width: "100%" }}
    />
  )
}

const CARD_PROPS_MAP = {
  [ARCHIVE_TYPES.BLOG]: {
    variant: () => "standard",
    headline: ["headline"],
    author: ["author"],
    image: ({ archive, image, description, headline }) => {
      if (archive) {
        return getArchiveImage(image, headline, description)
      }

      const fluid = getFluidGatsbyImage(
        { source: image, width: 800, height: 600 },
        { maxWidth: 600, quality: 90, isContentful: true }
      )
      return (
        <ResponsiveImage
          alt={description || headline || null}
          fluid={fluid}
          objectFit="cover"
          style={{ height: "100%", width: "100%" }}
        />
      )
    },
    imageSx: ({ isFilterLeft }) => ({
      height: [
        "195px",
        "265px",
        "265px",
        "265px",
        isFilterLeft ? "195px" : "265px",
      ],
      width: "100%",
      objectFit: "cover",
    }),
    meta: ({ categories, date }) => {
      const dateObj = dayjs(date)
      return [
        { text: `${dateObj.format("MMMM DD, YYYY")}`, color: "textDark" },
        ...(categories
          ? categories.map(category => ({
              text: category,
              color: "brandBrown",
            }))
          : []),
      ]
    },
    isLink: () => true,
    slug: ({ date, slug, permalink, archive = false }) => {
      if (archive && permalink) {
        return permalink
      }

      const dateObj = dayjs(date)
      return generateBlogSlug(
        permalink,
        dateObj.format("YYYY"),
        dateObj.format("MM")
      )
    },
  },
  [ARCHIVE_TYPES.PRESS]: {
    variant: () => "standard",
    headline: ["headline"],
    author: ["author"],
    image: ({ archive, image, description, headline }) => {
      if (archive) {
        return getArchiveImage(image, headline, description)
      }
      const fluid = getFluidGatsbyImage(
        { source: image, width: 800, height: 600 },
        { maxWidth: 600, quality: 90, isContentful: true }
      )
      return (
        <ResponsiveImage
          alt={description || headline || null}
          fluid={fluid}
          objectFit="cover"
          style={{ height: "100%", width: "100%" }}
        />
      )
    },
    imageSx: ({ isFilterLeft }) => ({
      height: [
        "195px",
        "265px",
        "265px",
        "265px",
        isFilterLeft ? "195px" : "265px",
      ],
      width: "100%",
      objectFit: "cover",
    }),
    meta: ({ categories, date }) => {
      const dateObj = dayjs(date)
      return [
        { text: `${dateObj.format("MMMM DD, YYYY")}`, color: "textDark" },
        ...(categories
          ? categories.map(category => ({
              text: category,
              color: "brandBrown",
            }))
          : []),
      ]
    },
    isLink: () => true,
    slug: ({ date, slug, permalink, archive = false }) => {
      if (archive && permalink) {
        return permalink
      }

      const dateObj = dayjs(date)
      return generateBlogSlug(
        permalink,
        dateObj.format("YYYY"),
        dateObj.format("MM")
      )
    },
  },
  [ARCHIVE_TYPES.PRESS_FEATURES]: {
    variant: () => "text",
    headline: ["headline"],
    author: ["author"],
    imageSx: ({ isFilterLeft }) => ({
      height: [
        "195px",
        "265px",
        "265px",
        "265px",
        isFilterLeft ? "195px" : "265px",
      ],
      width: "100%",
      objectFit: "cover",
    }),
    meta: ({ categories, date }) => {
      const dateObj = dayjs(date)
      return [
        { text: `${dateObj.format("MMMM YYYY")}`, color: "textDark" },
        ...(categories
          ? categories.map(category => ({
              text: category,
              color: "brandBrown",
            }))
          : []),
      ]
    },
    isLink: () => false,
    children: value => {
      if (value.pdf) {
        return (
          <Button
            variant="text"
            as={"a"}
            href={value.pdf}
            icon="download"
            iconPosition="right"
            colorScheme="brandBrown"
          >
            Download media
          </Button>
        )
      }
    },
  },
  [ARCHIVE_TYPES.WILDLIFE]: {
    variant: () => "standard",
    headline: ["headline"],
    author: ["author"],
    image: ({ archive, image, description, headline }) => {
      if (archive) {
        return getArchiveImage(image, headline, description)
      }
      const fluid = getFluidGatsbyImage(
        { source: image, width: 800, height: 600 },
        { maxWidth: 600, quality: 90, isContentful: true }
      )
      return (
        <ResponsiveImage
          alt={description || headline || null}
          fluid={fluid}
          objectFit="cover"
          style={{ height: "100%", width: "100%" }}
        />
      )
    },
    imageSx: ({ isFilterLeft }) => ({
      height: ["195px", "265px", "265px", isFilterLeft ? "195px" : "265px"],
      width: "100%",
      objectFit: "cover",
    }),
    meta: ({ categories, date }) => {
      const dateObj = dayjs(date)
      return [
        { text: `${dateObj.format("MMMM YYYY")}`, color: "textDark" },
        ...(categories
          ? categories.map(category => ({
              text: category,
              color: "brandBrown",
            }))
          : []),
      ]
    },
    isLink: () => true,
    slug: ({ date, slug, permalink, archive = false }) => {
      if (archive && permalink) {
        return permalink
      }

      const dateObj = dayjs(date)
      return generateBlogSlug(
        permalink,
        dateObj.format("YYYY"),
        dateObj.format("MM")
      )
    },
  },
  [ARCHIVE_TYPES.AWARDS]: {
    variant: () => "text",
    headline: ["headline"],
    subtitle: ["subtitle"],
    description: ["description"],
    meta: value => [
      {
        text: value.month ? `${value.month}, ${value.year}` : value.year,
        color: "brandBrown",
      },
    ],
  },
  [ARCHIVE_TYPES.CAREER]: {
    variant: () => "text",
    headline: ["headline"],
    description: ["description"],
    subtitle: value => (
      <Meta
        meta={[
          {
            text: value.location,
            color: "brandBrown",
          },
        ]}
      />
    ),
    children: value => (
      <Flex justifyContent="space-between">
        <Link
          to={`/career/${value.slug}`}
          state={{
            isModal: true,
          }}
        >
          <Button variant="text" colorScheme="brandBrown">
            Apply
          </Button>
        </Link>
        {value.details ? (
          <LinkWrapper
            variant="text"
            colorScheme="brandBrown"
            isDownload={true}
            to={value.details}
            label="View Details"
            external={true}
          />
        ) : null}
      </Flex>
    ),
  },
  [ARCHIVE_TYPES.AUTHOR]: {
    variant: () => "standard",
    headline: ["headline"],
    author: ["author"],
    image: ({ archive, image, description, headline }) => {
      if (archive) {
        return getArchiveImage(image, headline, description)
      }
      const fluid = getFluidGatsbyImage(
        { source: image, width: 800, height: 600 },
        { maxWidth: 600, quality: 90, isContentful: true }
      )
      return (
        <ResponsiveImage
          alt={description || headline || null}
          fluid={fluid}
          objectFit="cover"
          style={{ height: "100%", width: "100%" }}
        />
      )
    },
    imageSx: ({ isFilterLeft }) => ({
      height: [
        "195px",
        "265px",
        "265px",
        "265px",
        isFilterLeft ? "195px" : "265px",
      ],
      width: "100%",
      objectFit: "cover",
    }),
    meta: ({ categories, date }) => {
      const dateObj = dayjs(date)
      return [
        { text: `${dateObj.format("MMMM DD, YYYY")}`, color: "textDark" },
        ...(categories
          ? categories.map(category => ({
              text: category,
              color: "brandBrown",
            }))
          : []),
      ]
    },
    isLink: () => true,
    slug: ({ date, slug, permalink, archive = false }) => {
      if (archive && permalink) {
        return permalink
      }

      const dateObj = dayjs(date)
      return generateBlogSlug(
        permalink,
        dateObj.format("YYYY"),
        dateObj.format("MM")
      )
    },
  },
  [ARCHIVE_TYPES.DIGITAL_MAGAZINE]: {
    variant: () => "standard",
    headline: ["headline"],
    author: ["author"],
    image: ({ archive, image, description, headline }) => {
      if (archive) {
        return getArchiveImage(image, headline, description)
      }
      const fluid = getFluidGatsbyImage(
        { source: image, width: 800, height: 600 },
        { maxWidth: 600, quality: 90, isContentful: true }
      )
      return (
        <ResponsiveImage
          alt={description || headline || null}
          fluid={fluid}
          objectFit="cover"
          style={{ height: "100%", width: "100%" }}
        />
      )
    },
    imageSx: ({ isFilterLeft }) => ({
      height: [
        "380px",
        "400px",
        "400px",
        "1000px",
        isFilterLeft ? "380px" : "400px",
      ],
      width: "100%",
      objectFit: "cover",
    }),
    meta: ({ categories, date }) => {
      const dateObj = dayjs(date)
      return [
        { text: `${dateObj.format("MMMM YYYY")}`, color: "textDark" },
        ...(categories
          ? categories.map(category => ({
              text: category,
              color: "brandBrown",
            }))
          : []),
      ]
    },
    isLink: () => true,
    slug: ({ date, slug, permalink, archive = false }) => {
      if (archive && permalink) {
        return permalink
      }

      const dateObj = dayjs(date)
      return generateBlogSlug(
        permalink,
        dateObj.format("YYYY"),
        dateObj.format("MM")
      )
    },
  },
}

export const FILTER_POSITIONS = {
  TOP: "Top",
  LEFT: "Left",
  NONE: "No Filter",
}

const ConnectedSearchBox = connectSearchBox(({ currentRefinement, refine }) => (
  <Input
    type="input"
    placeholder="Search..."
    onChange={e => refine(e.target.value)}
    defaultValue={currentRefinement}
    bg="baseWhite"
  />
))

const getAllMenuItems = ({
  isVertical,
  currentRefinement,
  refine,
  items,
  existingRefinements,
  attribute,
  type = null,
  isBushStoriesPage = false,
  ...rest
}) => {
  return (
    <>
      <FilterItem
        active={!currentRefinement}
        isVertical={isVertical}
        onClick={() => refine()}
        size="small"
      >
        All
      </FilterItem>
      {items.map(item => {
        if (existingRefinements.includes(item.value)) return null
        return (
          <FilterItem
            key={item.value}
            isVertical={isVertical}
            active={item.isRefined}
            size="small"
          >
            {attribute === ATTRIBUTE_NAMES.category &&
            type === ARCHIVE_TYPES.BLOG &&
            !isBushStoriesPage &&
            useAllSitePages_PageExists(`/blog/${createSlug(item.label)}/`) ? (
              <Link to={`/blog/${createSlug(item.label)}/`}>{item.label}</Link>
            ) : attribute === ATTRIBUTE_NAMES.region &&
              type === ARCHIVE_TYPES.WILDLIFE &&
              useAllSitePages_PageExists(
                `/wildlife/${createSlug(
                  item.label === "Grumeti & Lamai" ? "Grumeti" : item.label
                )}/`
              ) ? (
              <Link
                to={`/wildlife/${createSlug(
                  item.label === "Grumeti & Lamai" ? "Grumeti" : item.label
                )}/`}
              >
                {item.label}
              </Link>
            ) : (
              <div onClick={() => refine(item.value)}>{item.label}</div>
            )}
          </FilterItem>
        )
      })}
    </>
  )
}

const getBlogMenuItems = ({
  isVertical,
  currentRefinement,
  refine,
  updateArticleType,
  bushStoriesPageExists,
  isBushStoriesPage,
}) => {
  return (
    <>
      <FilterItem
        active={currentRefinement === ARTICLE_TYPES.BLOG}
        isVertical={isVertical}
        size="small"
      >
        {isBushStoriesPage ? (
          <Link to="/blog/">Blog Posts</Link>
        ) : (
          <div
            onClick={() => {
              refine(ARTICLE_TYPES.BLOG)
              updateArticleType(ARTICLE_TYPES.BLOG)
            }}
          >
            Blog Posts
          </div>
        )}
      </FilterItem>
      <FilterItem
        active={currentRefinement === ARTICLE_TYPES.BUSH_STORY}
        isVertical={isVertical}
        size="small"
      >
        {bushStoriesPageExists ? (
          <Link to="/blog/bush-stories/">Bush Stories</Link>
        ) : (
          <div
            onClick={() => {
              refine(ARTICLE_TYPES.BUSH_STORY)
              updateArticleType(ARTICLE_TYPES.BUSH_STORY)
            }}
          >
            Bush Stories
          </div>
        )}
      </FilterItem>
    </>
  )
}

const getWLRMenuItems = ({
  isVertical,
  currentRefinement,
  refine,
  updateArticleType,
  bushStoriesPageExists,
}) => {
  return (
    <>
      <FilterItem
        active={currentRefinement === ARTICLE_TYPES.WILDLIFE}
        isVertical={isVertical}
        onClick={() => {
          refine(ARTICLE_TYPES.WILDLIFE)
          updateArticleType(ARTICLE_TYPES.WILDLIFE)
        }}
        size="small"
      >
        Wildlife Reports
      </FilterItem>
      <FilterItem
        active={currentRefinement === ARTICLE_TYPES.BUSH_STORY}
        isVertical={isVertical}
        size="small"
      >
        {bushStoriesPageExists ? (
          <Link to="/blog/bush-stories/">Bush Stories</Link>
        ) : (
          <div
            onClick={() => {
              refine(ARTICLE_TYPES.BUSH_STORY)
              updateArticleType(ARTICLE_TYPES.BUSH_STORY)
            }}
          >
            Bush Stories
          </div>
        )}
      </FilterItem>
    </>
  )
}

const ConnectedMenu = connectMenu(props => {
  const { isVertical, attribute, isWLR, pageContext } = props
  const bushStoriesPageExists = useAllSitePages_PageExists("/blog/bush-stories/")
  const isBushStoriesPage = pageContext?.basePath === "/blog/bush-stories/" ||
                            pageContext?.slug === "blog/bush-stories"

  return (
    <>
      <Media greaterThanOrEqual="lg">
        <Filter isVertical={isVertical}>
          {isWLR
            ? getWLRMenuItems({ ...props, bushStoriesPageExists })
            : attribute === "articleType"
            ? getBlogMenuItems({ ...props, bushStoriesPageExists, isBushStoriesPage })
            : getAllMenuItems({ ...props, isBushStoriesPage })}
        </Filter>
      </Media>
      <Media lessThan="lg">
        <Filter isVertical={false} hasScrollOverflow={true}>
          {isWLR
            ? getWLRMenuItems({ ...props, isVertical: false, bushStoriesPageExists })
            : attribute === "articleType"
            ? getBlogMenuItems({ ...props, isVertical: false, bushStoriesPageExists, isBushStoriesPage })
            : getAllMenuItems({ ...props, isVertical: false, isBushStoriesPage })}
        </Filter>
      </Media>
    </>
  )
})

const Hit = ({ type, ...props }) => {
  const cardProps = mapperWithFunction(props, CARD_PROPS_MAP[type])
  let slug = cardProps.slug
  if (slug && !slug.endsWith("/")) slug += "/"

  if (props.archive) {
    return (
      <a href={slug}>
        <Card {...cardProps} />
      </a>
    )
  }
  return (
    <>
      <Link to={slug}>
        <Card {...cardProps} />
      </Link>
    </>
  )
}

const ConnectedHits = connectHits(
  ({ hits, view, isFilterLeft, type, ...rest }) => {
    return (
      <GridContainer
        sx={{
          gridTemplateColumns: [
            "1fr",
            "1fr",
            "repeat(2, 1fr)",
            "repeat(3, 1fr)",
          ],
          gap: [4],
        }}
        width="100%"
      >
        {hits.map((hit, index) => (
          <GridItem key={hit.objectID} index={index} columns={[1, 1, 2, 3]}>
            <Hit {...hit} isFilterLeft={isFilterLeft} type={type} />
          </GridItem>
        ))}
      </GridContainer>
    )
  }
)

const ConnectedPagination = connectPagination(
  ({ nbPages, currentRefinement, refine, ...connectorProps }) => {
    const {
      sectionRef,
      onPageChangeStart,
      paginationInfo,
      basePath,
      ...rest
    } = connectorProps
    const {
      LinkComponent = Link,
      to,
      search,
      updatePageInUrl: customUpdatePageInUrl,
      ...paginationProps
    } = rest
    const containerRef = useRef(null)
    const firstLoad = useRef(true)
    const skipNextScroll = useRef(false)
    const hasUserPaginatedRef = useRef(false)

    const basePathForPagination = useMemo(() => {
      if (basePath) return basePath
      if (to) return to
      return "/"
    }, [basePath, to])

    const serverUpdatePageInUrl = useMemo(() => {
      if (!paginationInfo) return undefined
      return (_pathname, _search, page) =>
        buildSSRPagePath(basePathForPagination, { page })
    }, [paginationInfo, basePathForPagination])

    const scrollToSectionTop = useCallback(() => {
      if (typeof window === "undefined") return
      const target =
        sectionRef?.current ||
        containerRef.current?.closest(".ais-InstantSearch") ||
        containerRef.current?.parentElement?.parentElement
      if (target) {
        const yOffset = 0
        const y =
          target.getBoundingClientRect().top + window.pageYOffset + yOffset
        window.scrollTo({
          top: y > 0 ? y : 0,
          behavior: "smooth",
        })
      }
    }, [sectionRef])

    useEffect(() => {
      if (firstLoad.current) {
        firstLoad.current = false
        return
      }

      if (skipNextScroll.current) {
        skipNextScroll.current = false
        return
      }

      if (!hasUserPaginatedRef.current) {
        return
      }

      let rafId = null

      if (typeof window !== "undefined") {
        rafId = window.requestAnimationFrame(scrollToSectionTop)
      } else {
        scrollToSectionTop()
      }

      return () => {
        if (rafId && typeof window !== "undefined") {
          window.cancelAnimationFrame(rafId)
        }
      }
    }, [currentRefinement, nbPages, scrollToSectionTop])

    return (
      <Box ref={containerRef}>
        <Pagination
          currentPage={currentRefinement}
          numberOfPages={nbPages}
          goToPage={pageNumber => {
            skipNextScroll.current = true
            hasUserPaginatedRef.current = true
            scrollToSectionTop()
            if (typeof onPageChangeStart === "function") {
              onPageChangeStart(pageNumber)
            }
            refine(pageNumber)
          }}
          LinkComponent={LinkComponent}
          to={paginationInfo ? basePathForPagination : to}
          search={paginationInfo ? "" : search}
          updatePageInUrl={
            paginationInfo ? serverUpdatePageInUrl : customUpdatePageInUrl
          }
          {...paginationProps}
        />
      </Box>
    )
  }
)

function buildRefinements(refinements) {
  return (refinements || []).map(({ __typename, headline }, idx) => {
    const filterName = REFINEMENT_NAME_MAP[__typename]
    const isLast = idx === refinements.length - 1
    return `${filterName}:'${headline}'${!isLast ? " AND " : ""}`
  })
}

const HierarchicalMenu = ({ items, refine, createURL }) => (
  <Box mb={[3]}>
    <Filter isVertical={true}>
      {items.map(item => (
        <>
          <FilterItem
            href={createURL(item.value)}
            active={item.isRefined}
            isVertical={true}
            size="small"
            onClick={event => {
              event.preventDefault()
              refine(item.value)
            }}
          >
            {item.label} ({item.count})
          </FilterItem>
          <Box ml={[2]}>
            {item.items && (
              <HierarchicalMenu
                items={item.items.reverse()}
                refine={refine}
                createURL={createURL}
              />
            )}
          </Box>
        </>
      ))}
    </Filter>
  </Box>
)

const CustomHierarchicalMenu = connectHierarchicalMenu(HierarchicalMenu)

const monthNames = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
]

const transformMenuItems = (items, attribute) => {
  if (attribute === "articleType") {
    return orderBy(items, ["label"])
  } else if (attribute === "year") {
    return attribute === "year" ? orderBy(items, ["label"], ["desc"]) : items
  } else if (attribute === "year_hier.lvl0") {
    return items.sort((a, b) => {
      const aIdx = monthNames.findIndex(month => month === a.label)
      const bIdx = monthNames.findIndex(month => month === b.label)

      return aIdx > bIdx ? 1 : -1
    })
  }
  return items
}

const getDefaultRefinements = (type, attribute, defaultRefinement = null, archiveDefaultRefinement = null) => {
  switch (type) {
    case ARCHIVE_TYPES.BLOG:
      if (attribute === "articleType") {
        // Use archiveDefaultRefinement if provided (for programmatic pages like Bush Stories)
        return archiveDefaultRefinement || ARTICLE_TYPES.BLOG
      } else if (attribute === "category") {
        return defaultRefinement ? defaultRefinement.headline : null
      }
      break
    case ARCHIVE_TYPES.WILDLIFE:
      if (attribute === "articleType") {
        return ARTICLE_TYPES.WILDLIFE
      } else if (attribute === "region") {
        // Support both object with headline property and direct string values
        if (typeof defaultRefinement === "string") {
          return defaultRefinement
        }
        return defaultRefinement ? defaultRefinement.headline : null
      }
      break
    default:
      break
  }

  return "" // Return an empty string if no default refinement is found for the given type and attribute.
}

const ArchiveWrapper = ({
  type,
  filterPosition,
  isSearchable,
  headline,
  headlineLineTwo,
  description,
  defaultRefinement,
  refinements,
  pageContext = {},
}) => {
  const overrideDefaultRefinement = pageContext?.archiveDefaultRefinement
  const resolvedDefaultRefinement =
    typeof overrideDefaultRefinement !== "undefined"
      ? overrideDefaultRefinement
      : defaultRefinement

  const location = useLocation()
  const sectionRef = useRef(null)
  const suppressScrollRestoreRef = useRef(false)
  const debouncedSetStateRef = useRef(null)

  // Get site URL for absolute canonical URLs
  const { site } = useStaticQuery(siteQuery)
  const siteUrl = site?.siteMetadata?.siteUrl || "https://singita.com"
  const siteUrlClean = siteUrl.replace(/\/+$/, "") // Remove trailing slashes

  const paginationMap = useMemo(() => {
    if (!Array.isArray(pageContext?.archivePagination)) return {}
    return pageContext.archivePagination.reduce((acc, entry) => {
      if (!entry || !entry.type) return acc
      const normalizedBase = normalizeBasePath(
        entry.basePath || pageContext?.basePath || "/"
      )
      acc[entry.type] = {
        ...entry,
        basePath: normalizedBase,
      }
      return acc
    }, {})
  }, [pageContext?.archivePagination, pageContext?.basePath])

  // Try to get pagination info by type, or fallback to first entry if available
  const paginationInfo = useMemo(() => {
    // First try to match by type
    if (paginationMap[type]) {
      return paginationMap[type]
    }
    // Fallback: if there's only one entry in archivePagination array, use it
    if (
      Array.isArray(pageContext?.archivePagination) &&
      pageContext.archivePagination.length === 1
    ) {
      const entry = pageContext.archivePagination[0]
      if (entry) {
        const normalizedBase = normalizeBasePath(
          entry.basePath || pageContext?.basePath || "/"
        )
        return {
          ...entry,
          basePath: normalizedBase,
        }
      }
    }
    return undefined
  }, [
    paginationMap,
    type,
    pageContext?.archivePagination,
    pageContext?.basePath,
  ])

  const normalizedBasePath = useMemo(() => {
    if (paginationInfo?.basePath)
      return normalizeBasePath(paginationInfo.basePath)
    if (pageContext?.basePath) return normalizeBasePath(pageContext.basePath)
    return normalizeBasePath(location?.pathname || "/")
  }, [paginationInfo?.basePath, pageContext?.basePath, location?.pathname])

  const canonicalBasePath = useMemo(() => {
    if (paginationInfo?.basePath) return paginationInfo.basePath
    if (pageContext?.basePath) return pageContext.basePath
    return location?.pathname || "/"
  }, [paginationInfo?.basePath, pageContext?.basePath, location?.pathname])

  const [searchState, setSearchState] = useState(() => {
    const initialState = urlToSearchState(location)

    if (paginationInfo?.currentPage && paginationInfo.currentPage > 1) {
      return {
        ...initialState,
        page: paginationInfo.currentPage,
      }
    }
    return initialState
  })

  useEffect(() => {
    const nextState = urlToSearchState(location)

    if (paginationInfo?.currentPage && paginationInfo.currentPage > 1) {
      nextState.page = paginationInfo.currentPage
    } else if (paginationInfo && !nextState.page) {
      nextState.page = 1
    }
    setSearchState(prev => {
      const prevString = JSON.stringify(prev)
      const nextString = JSON.stringify(nextState)
      return prevString === nextString ? prev : nextState
    })
  }, [
    location.pathname,
    location.search,
    paginationInfo?.currentPage,
    paginationInfo,
  ])

  useEffect(() => {
    return () => clearTimeout(debouncedSetStateRef.current)
  }, [])

  const isServerPaginated = Boolean(paginationInfo)

  const canonicalUrl = useMemo(() => {
    let canonicalCandidate = ""

    // Always prefer the canonical URL from pageContext if available
    if (pageContext?.canonicalUrl) {
      canonicalCandidate = pageContext.canonicalUrl
    } else if (!isServerPaginated) {
      canonicalCandidate = normalizedBasePath
    } else {
      // For server-paginated pages, prioritize paginationInfo.currentPage over searchState.page
      // to avoid off-by-one errors when searchState gets out of sync
      const pageNumber = paginationInfo?.currentPage || searchState?.page || 1
      if (!pageNumber || pageNumber <= 1) {
        canonicalCandidate = paginationInfo?.basePath || normalizedBasePath
      } else {
        canonicalCandidate = buildSSRPagePath(
          paginationInfo?.basePath || canonicalBasePath,
          {
            page: pageNumber,
          }
        )
      }
    }

    return resolveCanonicalUrl(canonicalCandidate, siteUrlClean)
  }, [
    pageContext?.canonicalUrl,
    isServerPaginated,
    paginationInfo,
    searchState?.page,
    normalizedBasePath,
    canonicalBasePath,
    siteUrlClean,
  ])

  useEffect(() => {
    if (!isServerPaginated || !pageContext?.setCanonicalUrl) return

    // Don't override server-rendered canonical URL unless we have active filters
    if (pageContext?.canonicalUrl && !hasActiveFilters(searchState)) {
      return
    }

    pageContext.setCanonicalUrl(canonicalUrl)
  }, [canonicalUrl, isServerPaginated, pageContext, searchState])

  const onSearchStateChange = useCallback(
    updatedSearchState => {
      if (typeof window === "undefined") return
      const scrollY = window.scrollY
      clearTimeout(debouncedSetStateRef.current)

      setSearchState(updatedSearchState)

      debouncedSetStateRef.current = setTimeout(() => {
        if (isServerPaginated) {
          const nextHasFilters = hasActiveFilters(updatedSearchState)
          const currentFullUrl = `${window.location.pathname}${window.location.search}`
          if (nextHasFilters) {
            const targetQuery = buildQueryString(updatedSearchState, true)
            const targetUrl = targetQuery
              ? `${normalizedBasePath}${targetQuery}`
              : normalizedBasePath
            if (currentFullUrl !== targetUrl) {
              window.history.replaceState({}, "", targetUrl)
            }
          } else {
            const targetUrl = buildSSRPagePath(
              normalizedBasePath,
              updatedSearchState
            )
            if (currentFullUrl !== targetUrl) {
              window.history.replaceState({}, "", targetUrl)
            }
          }
        } else {
          window.history.replaceState(
            {},
            "",
            searchStateToUrl(updatedSearchState)
          )
        }

        if (!suppressScrollRestoreRef.current) {
          requestAnimationFrame(() => window.scrollTo(0, scrollY))
        } else {
          suppressScrollRestoreRef.current = false
        }
      }, DEBOUNCE_TIME)
    },
    [isServerPaginated, normalizedBasePath, suppressScrollRestoreRef]
  )

  useEffect(() => {
    const searchStateString = JSON.stringify(searchState)
    const urlSearchStateString = JSON.stringify(urlToSearchState(location))
    if (searchStateString !== urlSearchStateString) {
      setSearchState(urlToSearchState(location))
    }
  }, [location])

  const searchClient = algoliasearch(
    process.env.GATSBY_ALGOLIA_APP_ID,
    process.env.GATSBY_ALGOLIA_API_KEY
  )

  const indexName = ARCHIVE_INDEX_MAP[type]
  // Deprecate when everything has moved to new indices
  const postType = OLD_ARCHIVE_POST_TYPE_MAP[type]
  const articleType = NEW_ARCHIVE_POST_TYPE_MAP[type]
  const filters = ARCHIVE_FILTER_ATTRIBUTE_MAP[type]
  const isFilterLeft = filterPosition === FILTER_POSITIONS.LEFT
  const refinementsList = buildRefinements(refinements)
  const refinementValues = (refinements || []).map(({ headline }) => headline)

  const resolvedDefaultRefinementValue =
    typeof resolvedDefaultRefinement === "string"
      ? resolvedDefaultRefinement
      : resolvedDefaultRefinement &&
        typeof resolvedDefaultRefinement === "object"
      ? resolvedDefaultRefinement.headline || resolvedDefaultRefinement.name || ""
      : ""

  let filterString
  if (postType) {
    if (postType === "Wildlife Report") {
      filterString = `articleType:'${articleType}' OR articleType:'Bush Stories'${
        refinementsList.length > 0 ? ` AND ${refinementsList}` : ""
      }`
    } else filterString = `articleType:'${postType}'`
  } else if (articleType === ARCHIVE_TYPES.DIGITAL_MAGAZINE) {
    filterString = `articleType:'${articleType}'${
      refinementsList.length > 0 ? ` AND ${refinementsList}` : ""
    }`
  } else if (articleType) {
    filterString = `articleType:'${articleType}' OR articleType:'Bush Stories'${
      refinementsList.length > 0 ? ` AND ${refinementsList}` : ""
    }`
  } else if (type === ARCHIVE_TYPES.CAREER) {
    filterString = "isActive:true"
  } else if (type === ARCHIVE_TYPES.AUTHOR) {
    filterString = resolvedDefaultRefinementValue
      ? `author:'${resolvedDefaultRefinementValue}'`
      : ""
  } else {
    filterString = ""
  }
  const [currentArticleType, setArticleType] = React.useState(
    overrideDefaultRefinement || articleType
  )
  return (
    <Box as="section" ref={sectionRef}>
      <Container>
        <SectionHeading
          headline={headline}
          headlineLineTwo={headlineLineTwo}
          description={description ? description.description : null}
          direction={"row"}
          alignment={"left"}
        />
        <InstantSearch
          searchClient={searchClient}
          indexName={indexName}
          searchState={searchState}
          onSearchStateChange={onSearchStateChange}
          createURL={createURL}
        >
          <Configure hitsPerPage={9} filters={filterString} />
          <Flex
            mt={[5]}
            flexDirection={[
              "column",
              "column",
              "column",
              filterPosition === FILTER_POSITIONS.TOP ? "column" : "row",
            ]}
            alignItems="flex-start"
          >
            <Flex
              justifyContent={isFilterLeft ? "flex-start" : "space-between"}
              flexDirection={[
                "column",
                "column",
                isFilterLeft ? "column" : "row",
              ]}
              flexShrink="0"
              mr={isFilterLeft ? [0, 0, 0, 8] : [0]}
              mb={isFilterLeft ? [0] : [3]}
              sx={{
                position: [
                  "static",
                  "static",
                  "static",
                  isFilterLeft ? "sticky" : "static",
                ],
                top: "85px",
                left: 0,
              }}
              width={["100%", "100%", "auto"]}
            >
              {isSearchable && (
                <Box
                  mb={isFilterLeft ? [4] : [0]}
                  order={[0, 0, isFilterLeft ? 0 : 1]}
                  width={isFilterLeft ? "auto" : "300px"}
                >
                  {isFilterLeft && (
                    <Box mb={[1]}>
                      <Heading size="h6" fontWeight="bold">
                        Search
                      </Heading>
                    </Box>
                  )}
                  <ConnectedSearchBox />
                </Box>
              )}
              {filterPosition !== FILTER_POSITIONS.NONE && (
                <Box order={[1, 1, isFilterLeft ? 1 : 0]}>
                  {type === ARCHIVE_TYPES.BLOG
                    ? [
                        "articleType",
                        ...([ARTICLE_TYPES.BLOG, ""].includes(
                          currentArticleType
                        )
                          ? ["category"]
                          : ["region"]),
                      ].map(attribute => (
                        <Box key={attribute} mb={[5]}>
                          <Box mb={[3]}>
                            <Heading size="h6" fontWeight="bold">
                              Select {ATTRIBUTE_NAMES[attribute]}
                            </Heading>
                          </Box>
                          <Box pl={isFilterLeft ? [1] : [0]}>
                            <ConnectedMenu
                              limit={14}
                              existingRefinements={refinementValues}
                              defaultRefinement={getDefaultRefinements(
                                type,
                                attribute,
                                resolvedDefaultRefinement,
                                overrideDefaultRefinement
                              )}
                              isVertical={isFilterLeft}
                              attribute={attribute}
                              transformItems={items =>
                                transformMenuItems(items, attribute)
                              }
                              updateArticleType={articleType =>
                                setArticleType(articleType)
                              }
                              type={ARCHIVE_TYPES.BLOG}
                              pageContext={pageContext}
                            />
                          </Box>
                        </Box>
                      ))
                    : type === ARCHIVE_TYPES.WILDLIFE
                    ? ["articleType", "region", "year_hier.lvl0"].map(
                        attribute => (
                          <Box key={attribute} mb={[5]}>
                            <Box mb={[3]}>
                              <Heading size="h6" fontWeight="bold">
                                Select {ATTRIBUTE_NAMES[attribute]}
                              </Heading>
                            </Box>
                            <Box pl={isFilterLeft ? [1] : [0]}>
                              <ConnectedMenu
                                limit={attribute === "year_hier.lvl0" ? 12 : 10}
                                existingRefinements={refinementValues}
                                defaultRefinement={getDefaultRefinements(
                                  type,
                                  attribute,
                                  resolvedDefaultRefinement,
                                  overrideDefaultRefinement
                                )}
                                isVertical={isFilterLeft}
                                attribute={attribute}
                                transformItems={items =>
                                  transformMenuItems(items, attribute)
                                }
                                updateArticleType={articleType =>
                                  setArticleType(articleType)
                                }
                                isWLR={attribute === "articleType"}
                                type={ARCHIVE_TYPES.WILDLIFE}
                                pageContext={pageContext}
                              />
                            </Box>
                          </Box>
                        )
                      )
                    : filters.map((attribute, index) => {
                        const hasMultipleFilters = filters.length > 1
                        return (
                          <Box key={attribute} mb={[5]}>
                            {hasMultipleFilters && (
                              <Box mb={[3]}>
                                <Heading size="h6" fontWeight="bold">
                                  Select {ATTRIBUTE_NAMES[attribute]}
                                </Heading>
                              </Box>
                            )}
                            <Box
                              pl={
                                hasMultipleFilters && isFilterLeft ? [1] : [0]
                              }
                            >
                              <ConnectedMenu
                                limit={attribute === "year_hier.lvl0" ? 12 : 10}
                                existingRefinements={refinementValues}
                                defaultRefinement={getDefaultRefinements(
                                  type,
                                  attribute,
                                  resolvedDefaultRefinement,
                                  overrideDefaultRefinement
                                )}
                                isVertical={isFilterLeft}
                                attribute={attribute}
                                transformItems={items =>
                                  transformMenuItems(items, attribute)
                                }
                                updateArticleType={articleType =>
                                  setArticleType(articleType)
                                }
                                pageContext={pageContext}
                              />
                            </Box>
                          </Box>
                        )
                      })}
                </Box>
              )}
            </Flex>
            <Flex flexDirection="column" alignItems="center" width="100%">
              <ConnectedHits
                type={type}
                hitComponent={Hit}
                isFilterLeft={isFilterLeft}
              />
              <Box width={[1, 1, "auto", "auto"]} py={[3]}>
                <ConnectedPagination
                  sectionRef={sectionRef}
                  LinkComponent={Link}
                  to={location.pathname}
                  search={location.search}
                  paginationInfo={paginationInfo}
                  basePath={normalizedBasePath}
                  onPageChangeStart={() => {
                    suppressScrollRestoreRef.current = true
                  }}
                />
              </Box>
            </Flex>
          </Flex>
        </InstantSearch>
      </Container>
    </Box>
  )
}

export default ArchiveWrapper

export const archiveFields = graphql`
  fragment ArchiveFields on ContentfulLayoutArchive {
    id
    type
    filterPosition
    isSearchable
    headline
    headlineLineTwo
    description {
      description
    }
    defaultRefinement {
      headline
    }
  }
`

const QS_OPTIONS = {
  addQueryPrefix: true,
  encode: true,
  skipNulls: true,
}

const ensureLeadingSlash = value =>
  typeof value === "string" && value.startsWith("/") ? value : `/${value || ""}`

const ensureTrailingSlash = value =>
  typeof value === "string" && value.endsWith("/") ? value : `${value || ""}/`

const stripPageSegment = value =>
  typeof value === "string" ? value.replace(/\/page\/\d+\/?$/, "/") : value

const normalizeBasePath = value => {
  if (!value) return "/"
  let normalized = ensureLeadingSlash(value)
  normalized = stripPageSegment(normalized)
  normalized = normalized.replace(/\/{2,}/g, "/")
  normalized = normalized === "/" ? "/" : ensureTrailingSlash(normalized)
  return normalized || "/"
}

const sanitizeSearchState = state => {
  if (!state || typeof state !== "object") return {}
  const sanitized = {}
  for (const [key, value] of Object.entries(state)) {
    if (key === "page" || key === "configure") continue
    if (value === null || value === undefined) continue
    if (typeof value === "string" && value.trim() === "") continue
    if (Array.isArray(value) && value.length === 0) continue
    if (
      typeof value === "object" &&
      !Array.isArray(value) &&
      Object.keys(value).length === 0
    ) {
      continue
    }
    sanitized[key] = value
  }
  return sanitized
}

const hasActiveFilters = state =>
  Object.keys(sanitizeSearchState(state)).length > 0

const buildQueryString = (state, includePage = true) => {
  if (!state) return ""
  const queryState = sanitizeSearchState(state)
  if (includePage && state.page && Number(state.page) > 1) {
    queryState.page = Number(state.page)
  }
  const query = qs.stringify(queryState, QS_OPTIONS)
  return query
}

const buildSSRPagePath = (basePath, state) => {
  const normalizedBase = normalizeBasePath(basePath)
  const pageNumber = Number(state?.page || 1)
  if (!pageNumber || pageNumber <= 1) {
    return normalizedBase
  }
  return `${normalizedBase}page/${pageNumber}/`
}

// Resolve canonical values into absolute URLs while tolerating relative inputs.
const resolveCanonicalUrl = (value, siteUrlClean) => {
  const baseSiteUrl =
    typeof siteUrlClean === "string" && siteUrlClean.trim()
      ? siteUrlClean.replace(/\/+$/, "")
      : ""
  const fallback = baseSiteUrl ? `${baseSiteUrl}/` : "/"

  if (value === null || value === undefined) {
    return fallback
  }

  const candidate = `${value}`.trim()
  if (!candidate) {
    return fallback
  }

  try {
    return new URL(candidate).href
  } catch (error) {
    // Intentionally fall through – candidate is relative or protocol-relative
  }

  let baseHref = fallback
  if (baseSiteUrl) {
    try {
      const baseUrl = new URL(baseSiteUrl)
      const pathname = baseUrl.pathname.endsWith("/")
        ? baseUrl.pathname
        : `${baseUrl.pathname}/`
      baseHref = `${baseUrl.origin}${pathname}`
    } catch (error) {
      baseHref = fallback
    }
  }

  try {
    return new URL(candidate, baseHref).href
  } catch (error) {
    // One last attempt with an enforced leading slash for malformed relative paths
    try {
      const normalizedCandidate = candidate.startsWith("/")
        ? candidate
        : `/${candidate}`
      return new URL(normalizedCandidate, baseHref).href
    } catch (finalError) {
      return fallback
    }
  }
}

const siteQuery = graphql`
  query ArchiveSiteMetadata {
    site {
      siteMetadata {
        siteUrl
      }
    }
  }
`
